import React, { useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Navigate } from 'react-router-dom';
import { UserIcon, UsersIcon, PlusIcon, EditIcon, TrashIcon, CheckIcon, XIcon, RefreshCwIcon } from 'lucide-react';
// Mock users for demonstration
const INITIAL_USERS = [{
  id: '1',
  username: 'admin',
  name: 'Admin User',
  role: 'admin' as const,
  email: 'admin@example.com',
  lastLogin: '2023-06-10T14:30:00Z'
}, {
  id: '2',
  username: 'user',
  name: 'Regular User',
  role: 'user' as const,
  email: 'user@example.com',
  lastLogin: '2023-06-09T10:15:00Z'
}, {
  id: '3',
  username: 'john.doe',
  name: 'John Doe',
  role: 'user' as const,
  email: 'john.doe@example.com',
  lastLogin: '2023-06-08T09:20:00Z'
}];
const UserManagementPage: React.FC = () => {
  const {
    isAdmin
  } = useAuth();
  const [users, setUsers] = useState(INITIAL_USERS);
  const [isAdding, setIsAdding] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [editingUserId, setEditingUserId] = useState<string | null>(null);
  // Form state for new/editing user
  const [formData, setFormData] = useState({
    username: '',
    name: '',
    email: '',
    role: 'user' as 'admin' | 'user',
    password: ''
  });
  // Redirect non-admin users
  if (!isAdmin) {
    return <Navigate to="/dashboard" data-id="element-335" />;
  }
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const {
      name,
      value
    } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  const resetForm = () => {
    setFormData({
      username: '',
      name: '',
      email: '',
      role: 'user',
      password: ''
    });
  };
  const handleAddUser = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      // Simulate API call delay
      await new Promise(resolve => setTimeout(resolve, 1000));
      const newUser = {
        id: `${Date.now()}`,
        username: formData.username,
        name: formData.name,
        role: formData.role,
        email: formData.email,
        lastLogin: '-'
      };
      setUsers(prev => [...prev, newUser]);
      setIsAdding(false);
      resetForm();
    } catch (error) {
      console.error('Failed to add user:', error);
    } finally {
      setIsLoading(false);
    }
  };
  const handleEditUser = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingUserId) return;
    setIsLoading(true);
    try {
      // Simulate API call delay
      await new Promise(resolve => setTimeout(resolve, 1000));
      setUsers(prev => prev.map(user => user.id === editingUserId ? {
        ...user,
        username: formData.username,
        name: formData.name,
        role: formData.role,
        email: formData.email
      } : user));
      setEditingUserId(null);
      resetForm();
    } catch (error) {
      console.error('Failed to update user:', error);
    } finally {
      setIsLoading(false);
    }
  };
  const startEditing = (user: (typeof users)[0]) => {
    setEditingUserId(user.id);
    setFormData({
      username: user.username,
      name: user.name,
      email: user.email,
      role: user.role,
      password: '' // Don't include password when editing
    });
  };
  const cancelEditing = () => {
    setEditingUserId(null);
    resetForm();
  };
  const handleDeleteUser = async (userId: string) => {
    if (!window.confirm('Are you sure you want to delete this user?')) return;
    try {
      // Simulate API call delay
      await new Promise(resolve => setTimeout(resolve, 500));
      setUsers(prev => prev.filter(user => user.id !== userId));
    } catch (error) {
      console.error('Failed to delete user:', error);
    }
  };
  return <div className="space-y-6" data-id="element-336">
      <div className="flex justify-between items-center" data-id="element-337">
        <h2 className="text-2xl font-bold" data-id="element-338">User Management</h2>
        <button onClick={() => setIsAdding(true)} className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors" disabled={isAdding || editingUserId !== null} data-id="element-339">
          <PlusIcon className="h-4 w-4 mr-2" data-id="element-340" />
          Add User
        </button>
      </div>
      {/* Add/Edit User Form */}
      {(isAdding || editingUserId) && <div className="bg-white rounded-lg shadow p-4" data-id="element-341">
          <h3 className="text-lg font-semibold mb-4" data-id="element-342">
            {isAdding ? 'Add New User' : 'Edit User'}
          </h3>
          <form onSubmit={isAdding ? handleAddUser : handleEditUser} className="space-y-4 max-w-md" data-id="element-343">
            <div data-id="element-344">
              <label htmlFor="username" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-345">
                Username
              </label>
              <input type="text" id="username" name="username" required className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500" value={formData.username} onChange={handleInputChange} data-id="element-346" />
            </div>
            <div data-id="element-347">
              <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-348">
                Full Name
              </label>
              <input type="text" id="name" name="name" required className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500" value={formData.name} onChange={handleInputChange} data-id="element-349" />
            </div>
            <div data-id="element-350">
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-351">
                Email
              </label>
              <input type="email" id="email" name="email" required className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500" value={formData.email} onChange={handleInputChange} data-id="element-352" />
            </div>
            <div data-id="element-353">
              <label htmlFor="role" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-354">
                Role
              </label>
              <select id="role" name="role" required className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500" value={formData.role} onChange={handleInputChange} data-id="element-355">
                <option value="user" data-id="element-356">User</option>
                <option value="admin" data-id="element-357">Admin</option>
              </select>
            </div>
            {isAdding && <div data-id="element-358">
                <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-359">
                  Password
                </label>
                <input type="password" id="password" name="password" required={isAdding} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500" value={formData.password} onChange={handleInputChange} data-id="element-360" />
              </div>}
            <div className="flex space-x-2 pt-2" data-id="element-361">
              <button type="submit" className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors" disabled={isLoading} data-id="element-362">
                {isLoading ? <>
                    <RefreshCwIcon className="h-4 w-4 mr-2 animate-spin" data-id="element-363" />
                    {isAdding ? 'Adding...' : 'Saving...'}
                  </> : <>
                    <CheckIcon className="h-4 w-4 mr-2" data-id="element-364" />
                    {isAdding ? 'Add User' : 'Save Changes'}
                  </>}
              </button>
              <button type="button" onClick={isAdding ? () => setIsAdding(false) : cancelEditing} className="flex items-center px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 transition-colors" disabled={isLoading} data-id="element-365">
                <XIcon className="h-4 w-4 mr-2" data-id="element-366" />
                Cancel
              </button>
            </div>
          </form>
        </div>}
      {/* Users Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden" data-id="element-367">
        <div className="p-4 border-b border-gray-200" data-id="element-368">
          <div className="flex items-center" data-id="element-369">
            <UsersIcon className="h-5 w-5 text-blue-600 mr-2" data-id="element-370" />
            <h3 className="text-lg font-semibold" data-id="element-371">User Accounts</h3>
          </div>
        </div>
        <div className="overflow-x-auto" data-id="element-372">
          <table className="min-w-full divide-y divide-gray-200" data-id="element-373">
            <thead className="bg-gray-50" data-id="element-374">
              <tr data-id="element-375">
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-376">
                  User
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-377">
                  Email
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-378">
                  Role
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-379">
                  Last Login
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-380">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200" data-id="element-381">
              {users.map(user => <tr key={user.id} data-id="element-382">
                  <td className="px-6 py-4 whitespace-nowrap" data-id="element-383">
                    <div className="flex items-center" data-id="element-384">
                      <div className="flex-shrink-0 h-10 w-10 flex items-center justify-center rounded-full bg-blue-100 text-blue-600" data-id="element-385">
                        <UserIcon className="h-6 w-6" data-id="element-386" />
                      </div>
                      <div className="ml-4" data-id="element-387">
                        <div className="text-sm font-medium text-gray-900" data-id="element-388">
                          {user.name}
                        </div>
                        <div className="text-sm text-gray-500" data-id="element-389">
                          @{user.username}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-390">
                    {user.email}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap" data-id="element-391">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${user.role === 'admin' ? 'bg-purple-100 text-purple-800' : 'bg-green-100 text-green-800'}`} data-id="element-392">
                      {user.role}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-393">
                    {user.lastLogin !== '-' ? new Date(user.lastLogin).toLocaleString() : '-'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium" data-id="element-394">
                    <button onClick={() => startEditing(user)} disabled={isAdding || editingUserId !== null} className="text-blue-600 hover:text-blue-900 mr-3 disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-395">
                      <EditIcon className="h-4 w-4" data-id="element-396" />
                    </button>
                    <button onClick={() => handleDeleteUser(user.id)} disabled={isAdding || editingUserId !== null || user.username === 'admin'} className={`${user.username === 'admin' ? 'text-gray-400 cursor-not-allowed' : 'text-red-600 hover:text-red-900 disabled:opacity-50 disabled:cursor-not-allowed'}`} data-id="element-397">
                      <TrashIcon className="h-4 w-4" data-id="element-398" />
                    </button>
                  </td>
                </tr>)}
            </tbody>
          </table>
        </div>
      </div>
    </div>;
};
export default UserManagementPage;